import {
  ChartDataPoint,
  ChartDataResponse,
  useChartData,
  usePreviewChartData,
  useSite,
  useSiteLocationList,
} from "@inrange/building-manager-api-client";
import {
  Site,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import { buildSiteCalculationPayload } from "@inrange/shared-components";
import { sortBy } from "@inrange/utils";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { Link } from "react-router-dom";
import { useReactQueryError } from "../../../utils/react-query-error";
import CollapsibleCard from "../CollapsibleCard";
import ESysSelect from "./ESysSelect";
import { formatNumber0dp } from "./utils";

const colors = {
  //
  // Main site
  //
  mainfdemand: "#1f3c88",
  mainfgeneration: "#d35400",
  mainfbehindmeterpv: "#fdee1eff",
  mainfbatteryfrompv: "#ffa569ff",
  mainfbatteryfromgrid: "#b469ffff",
  mainfbatteryfinalcharge: "#777777",
  mainfbehindmeterbattery: "#ff9925ff",
  mainfbehindmeterbatterygrid: "#2fffafff",
  mainfcurtailed: "#6e4f4cff",
  mainfexportnetwork: "#ff3df5ff",
  mainfexportgrid: "#6776ffff",
  mainfimportgrid: "#c2e2ffff",

  //
  // Compare site (darker versions of main colors)
  //
  comparefdemand: "#192f68",
  comparefgeneration: "#9b3900",
  comparefbehindmeterpv: "#b4ba17",
  comparefbatteryfrompv: "#b36c4d",
  comparefbatteryfromgrid: "#794ecb",
  comparefbatteryfinalcharge: "#555555",
  comparefbehindmeterbattery: "#b3781e",
  comparefbehindmeterbatterygrid: "#26bfaf",
  comparefcurtailed: "#553936",
  comparefexportnetwork: "#a62ecc",
  comparefexportgrid: "#4f5aa3",
  comparefimportgrid: "#7fa8b3",
};

// Define preset periods with their date ranges and labels
const timePresets = [
  {
    key: "full-year",
    label: "Full Year",
    start: "2024-01-01",
    end: "2024-12-31",
  },
  { key: "jan", label: "January", start: "2024-01-01", end: "2024-01-31" },
  { key: "feb", label: "February", start: "2024-02-01", end: "2024-02-29" },
  { key: "mar", label: "March", start: "2024-03-01", end: "2024-03-31" },
  { key: "apr", label: "April", start: "2024-04-01", end: "2024-04-30" },
  { key: "may", label: "May", start: "2024-05-01", end: "2024-05-31" },
  { key: "jun", label: "June", start: "2024-06-01", end: "2024-06-30" },
  { key: "jul", label: "July", start: "2024-07-01", end: "2024-07-31" },
  { key: "aug", label: "August", start: "2024-08-01", end: "2024-08-31" },
  { key: "sep", label: "September", start: "2024-09-01", end: "2024-09-30" },
  { key: "oct", label: "October", start: "2024-10-01", end: "2024-10-31" },
  { key: "nov", label: "November", start: "2024-11-01", end: "2024-11-30" },
  { key: "dec", label: "December", start: "2024-12-01", end: "2024-12-31" },
  {
    key: "feb-14",
    label: "First 14 days of Feb",
    start: "2024-02-01",
    end: "2024-02-14",
  },
  {
    key: "aug-14",
    label: "First 14 days of Aug",
    start: "2024-08-01",
    end: "2024-08-14",
  },
];

// Helper function to aggregate data by day or month using site timezone
const aggregateData = (
  data: ChartDataPoint[],
  aggregationType: "half-hourly" | "daily" | "monthly",
  timeZone: string
): ChartDataPoint[] => {
  if (aggregationType === "half-hourly") {
    return data;
  }

  // Default to Europe/London if timezone is blank
  const tz = timeZone || "Europe/London";
  const aggregated = new Map<string, ChartDataPoint>();

  // Create reusable formatters for better performance
  const yearFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric",
  });
  const monthFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    month: "2-digit",
  });
  const dayFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    day: "2-digit",
  });

  data.forEach((point) => {
    // Convert UTC timestamp to local time in the site's timezone
    const utcDate = new Date(point.x);

    // Get the local date components in the site's timezone
    const year = yearFormatter.format(utcDate);
    const month = monthFormatter.format(utcDate);
    const day = dayFormatter.format(utcDate);

    let key: string;

    if (aggregationType === "daily") {
      // Group by day: YYYY-MM-DD (local time)
      key = `${year}-${month}-${day}`;
    } else {
      // Group by month: YYYY-MM (local time)
      key = `${year}-${month}`;
    }

    if (!aggregated.has(key)) {
      // Initialize with the first data point for this period
      aggregated.set(key, {
        x: key,
        fGeneration: 0,
        fDemand: 0,
        fBehindMeterPV: 0,
        fBatteryFromPV: 0,
        fBatteryFromGrid: 0,
        fBatteryFinalCharge: 0,
        fBehindMeterBattery: 0,
        fBehindMeterBatteryGrid: 0,
        fCurtailed: 0,
        fExportNetwork: 0,
        fExportGrid: 0,
        fImportGrid: 0,
      });
    }

    const agg = aggregated.get(key)!;
    // Sum all energy values
    agg.fGeneration += point.fGeneration || 0;
    agg.fDemand += point.fDemand || 0;
    agg.fBehindMeterPV += point.fBehindMeterPV || 0;
    agg.fBatteryFromPV += point.fBatteryFromPV || 0;
    agg.fBatteryFromGrid += point.fBatteryFromGrid || 0;
    agg.fBehindMeterBattery += point.fBehindMeterBattery || 0;
    agg.fBehindMeterBatteryGrid += point.fBehindMeterBatteryGrid || 0;
    agg.fCurtailed = (agg.fCurtailed ?? 0) + (point.fCurtailed || 0);
    agg.fExportNetwork += point.fExportNetwork || 0;
    agg.fExportGrid += point.fExportGrid || 0;
    agg.fImportGrid += point.fImportGrid || 0;
    // For battery final charge, use the last value (not sum)
    agg.fBatteryFinalCharge = point.fBatteryFinalCharge || 0;
  });

  return Array.from(aggregated.values()).sort((a, b) => a.x.localeCompare(b.x));
};

const ChartForecast = ({ site }: { site: SiteAllowStringValues }) => {
  const [startDate, setStartDate] = useState<string>("2024-01-01");
  const [endDate, setEndDate] = useState<string>("2024-12-31");
  const [timePeriod, setTimePeriod] = useState<string>(
    "full-year|2024-01-01|2024-12-31"
  );
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [chartWidth, setChartWidth] = useState<string>("100%");
  const [selectedCompareSite, setSelectedCompareSite] = useState<any[]>([]);
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});
  const [hiddenPrefix, setHiddenPrefix] = useState<Record<string, boolean>>({});
  const [selectedEsysIdx, setSelectedEsysIdx] = useState<string>("all");
  const [selectedCompareSiteEsysIdx, setSelectedCompareSiteEsysIdx] =
    useState<string>("all");
  const [aggregationType, setAggregationType] = useState<
    "half-hourly" | "daily" | "monthly"
  >("half-hourly");

  const esysCount = Object.values(site.esys).filter((m) => !!m).length;
  useEffect(() => {
    setSelectedEsysIdx("all");
  }, [esysCount]);

  const { fetchSiteReportPreview } = useSite({
    app: "admin",
  });
  useReactQueryError(fetchSiteReportPreview, "Error downloading report");

  // Fetch site list for comparison
  const { fetchSites } = useSiteLocationList();
  const siteOptions = [...((fetchSites.data?.sites || []) as unknown as Site[])]
    .filter((s) => s.id !== site.id)
    .sort(sortBy((s) => s.name));

  // Get the selected comparison site object
  const compareSite = selectedCompareSite[0];

  const chartDataQuery = usePreviewChartData({
    dataType: "energy_flows",
    payload: { site },
    options: { esysIdx: selectedEsysIdx },
    enabled: shouldFetch,
  });
  const { fetchSite: fetchCompareSite } = useSite({
    app: "admin",
    siteId: compareSite?.id,
    enableNonEssentialQueries: false,
  });
  const compareChartDataQuery = useChartData({
    dataType: "energy_flows",
    siteId: compareSite?.id,
    options: { esysIdx: selectedCompareSiteEsysIdx },
    enabled: shouldFetch && !!compareSite,
  });

  // isInitialLoading, not isLoading: both queries are enabled only when
  // shouldFetch is set (and the compare query also needs a compareSite), so they
  // start disabled. In @tanstack/react-query v4 a disabled query reports
  // isLoading: true forever, which would permanently disable the Enable button
  // ("Loading...") and the chart could never be turned on. isInitialLoading is
  // false for a disabled query and true only while a first fetch is in flight.
  const isLoading =
    chartDataQuery.isInitialLoading || compareChartDataQuery.isInitialLoading;
  const error = chartDataQuery.error || compareChartDataQuery.error;

  const compareChartDataQueryData =
    compareChartDataQuery.data as ChartDataResponse;

  // Filter and format data for the chart
  const chartData = useMemo(() => {
    const series: {
      id: string;
      prefix: string;
      name: string;
      color: string;
      siteId: string;
      data: { x: Date; y: number }[];
      defaultHidden: boolean;
      stacked: boolean;
      axis: string;
      groups: string[];
    }[] = [];

    const addSeriesForSite = (
      site: Site,
      dataPoints: ChartDataPoint[],
      idPrefix: string
    ) => {
      // Apply aggregation to the data points using site's timezone
      const aggregatedData = aggregateData(
        dataPoints,
        aggregationType,
        site.timeZone
      );

      // The backend omits `fCurtailed` for sites with zero forecast curtailment.
      const hasCurtailed = dataPoints.some((p) => p.fCurtailed !== undefined);

      for (const chartField of [
        {
          field: "fDemand",
          label: "Demand",
          showTotal: true,
          defaultVisible: true,
          stacked: false,
          axis: "left",
          groups: ["Demand"],
        },
        {
          field: "fGeneration",
          label: "Generation",
          showTotal: true,
          defaultVisible: true,
          stacked: false,
          axis: "left",
          groups: ["Generation", "Export"],
        },
        {
          field: "fBehindMeterPV",
          label: "Behind Meter PV",
          showTotal: true,
          defaultVisible: false,
          stacked: true,
          axis: "left",
          groups: ["Demand", "Generation"],
        },
        {
          field: "fBehindMeterBattery",
          label: "Behind Meter From Battery (PV)",
          showTotal: true,
          defaultVisible: false,
          stacked: true,
          axis: "left",
          groups: ["Demand"],
        },
        {
          field: "fBehindMeterBatteryGrid",
          label: "Behind Meter From Battery (Grid)",
          showTotal: true,
          defaultVisible: false,
          stacked: true,
          axis: "left",
          groups: ["Demand"],
        },
        {
          field: "fBatteryFromPV",
          label: "Battery Charge From PV",
          showTotal: true,
          defaultVisible: false,
          stacked: true,
          axis: "left",
          groups: ["Generation", "Battery Charge"],
        },
        {
          field: "fBatteryFromGrid",
          label: "Battery Charge From Grid",
          showTotal: true,
          defaultVisible: false,
          stacked: true,
          axis: "left",
          groups: ["Battery Charge"],
        },
        {
          field: "fBatteryFinalCharge",
          label: "Battery Final Charge",
          showTotal: false,
          defaultVisible: false,
          stacked: false,
          axis: "right",
          groups: ["Battery Charge"],
        },
        {
          field: "fExportNetwork",
          label: "Export Network",
          showTotal: true,
          defaultVisible: false,
          stacked: true,
          axis: "left",
          groups: ["Generation", "Export"],
        },
        {
          field: "fExportGrid",
          label: "Exported (spill)",
          showTotal: true,
          defaultVisible: false,
          stacked: true,
          axis: "left",
          groups: ["Generation", "Export"],
        },
        {
          field: "fCurtailed",
          label: "Curtailed",
          showTotal: true,
          defaultVisible: false,
          stacked: true,
          axis: "left",
          groups: ["Generation", "Export"],
        },
        {
          field: "fImportGrid",
          label: "Grid Import",
          showTotal: true,
          defaultVisible: false,
          stacked: true,
          axis: "left",
          groups: ["Demand"],
        },
      ]) {
        if (chartField.field === "fCurtailed" && !hasCurtailed) continue;
        const total = chartField.showTotal
          ? aggregatedData.reduce(
              (sum, point) => sum + (point[chartField.field] || 0),
              0
            )
          : 0;
        series.push({
          id: `${idPrefix}${chartField.field}`,
          prefix: idPrefix,
          siteId: site.id,
          name: `${chartField.label}${chartField.showTotal ? ` (${formatNumber0dp(total)} kWh)` : ""}`,
          color: colors[`${idPrefix}${chartField.field}`.toLowerCase()],
          data: aggregatedData.map((point) => ({
            x: new Date(point.x),
            y: point[chartField.field] || 0,
          })),
          defaultHidden: !chartField.defaultVisible,
          stacked: chartField.stacked,
          axis: chartField.axis,
          groups: chartField.groups,
        });
      }
    };

    // Process main site energy flows data
    if (chartDataQuery.data?.data) {
      const filteredData = chartDataQuery.data.data.filter((point) => {
        if (!startDate || !endDate) return true;
        const pointDate = new Date(point.x).toISOString().split("T")[0];
        return pointDate >= startDate && pointDate <= endDate;
      });

      addSeriesForSite(site as unknown as Site, filteredData, "main");
    }

    // Process comparison site demand data
    if (compareChartDataQueryData?.data && compareSite) {
      const filteredData = (
        compareChartDataQueryData.data as ChartDataPoint[]
      ).filter((point) => {
        if (!startDate || !endDate) return true;
        const pointDate = new Date(point.x).toISOString().split("T")[0];
        return pointDate >= startDate && pointDate <= endDate;
      });

      addSeriesForSite(
        compareSite as unknown as Site,
        filteredData as ChartDataPoint[],
        "compare"
      );
    }

    return series;
  }, [
    chartDataQuery.data?.data,
    compareChartDataQueryData?.data,
    compareSite,
    site,
    startDate,
    endDate,
    aggregationType,
  ]);

  const handleTimePeriodChange = (period: string) => {
    setTimePeriod(period);

    // Parse dates from the period value if it contains dates
    if (period.includes("|")) {
      const [_periodKey, startDate, endDate] = period.split("|");
      setStartDate(startDate);
      setEndDate(endDate);
    }
    // "custom" case - don't change dates
  };

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    // Find matching preset
    const matchingPreset = timePresets.find(
      (preset) => preset.start === newStartDate && preset.end === newEndDate
    );

    setTimePeriod(
      matchingPreset
        ? `${matchingPreset.key}|${matchingPreset.start}|${matchingPreset.end}`
        : "custom"
    );
  };

  const toggleSeries = (seriesId: string) => {
    setHiddenSeries((prev) => ({
      ...prev,
      [seriesId]: !(
        prev[seriesId] ??
        chartData.find((s) => s.id === seriesId)?.defaultHidden
      ),
    }));
  };

  // Chart component that can be reused in both inline and modal views
  const ChartComponent = ({ height = "400px" }: { height?: string }) => {
    const filteredChartData = chartData.filter(
      (series) =>
        !(hiddenSeries[series.id] ?? series.defaultHidden) &&
        !(hiddenPrefix[series.prefix] ?? false)
    );

    // Create stacking groups for left and right axes
    const leftStackedSeries = new Set<string>();
    const rightStackedSeries = new Set<string>();

    filteredChartData.forEach((series) => {
      if (series.stacked) {
        if (series.axis === "right") {
          rightStackedSeries.add(series.prefix);
        } else {
          leftStackedSeries.add(series.prefix);
        }
      }
    });

    // Calculate midnight timestamps for vertical lines
    const midnightLines: { xAxis: number }[] = [];
    const midnightStart = new Date(startDate);
    const midnightEnd = new Date(endDate);

    // Calculate the number of days in the range
    const daysDiff = Math.ceil(
      (midnightEnd.getTime() - midnightStart.getTime()) / (1000 * 60 * 60 * 24)
    );

    // If more than 31 days, show lines every 7 days; otherwise show daily
    const dayInterval = daysDiff > 31 ? 7 : 1;

    // Start from the day after startDate's midnight
    const midnightCurrent = new Date(midnightStart);
    midnightCurrent.setUTCHours(0, 0, 0, 0);
    midnightCurrent.setUTCDate(midnightCurrent.getUTCDate() + dayInterval);

    while (midnightCurrent <= midnightEnd) {
      midnightLines.push({ xAxis: midnightCurrent.getTime() });
      midnightCurrent.setUTCDate(midnightCurrent.getUTCDate() + dayInterval);
    }

    const echartsOptions = {
      animation: false, // Disable all animations for immediate updates
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
        },
        formatter: (params: any) => {
          if (!params || params.length === 0) return "";

          const date = new Date(params[0].axisValue);
          let dateLabel = "";
          if (aggregationType === "monthly") {
            dateLabel = echarts.time.format(
              date.getTime(),
              "{MMM} {yyyy}",
              false
            );
          } else if (aggregationType === "daily") {
            dateLabel = echarts.time.format(
              date.getTime(),
              "{dd} {MMM} {yyyy}",
              false
            );
          } else {
            dateLabel =
              date.toLocaleDateString() +
              " " +
              date.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              });
          }
          let tooltip = `<strong>${dateLabel}</strong><br/>`;

          params.forEach((param: any) => {
            tooltip += `${param.marker} ${param.seriesName}: ${param.value[1]?.toFixed(2) || 0} kWh<br/>`;
          });

          return tooltip;
        },
      },
      legend: {
        show: false, // We'll use our custom legend component
      },
      // Remove horizontal spacing between bars - force overlap to eliminate any gaps
      barCategoryGap: -1, // Negative gap to force slight overlap between time periods
      barGap: -1, // Negative gap to force slight overlap between series bars
      grid: {
        left: "20px",
        right: "40px",
        bottom: "40px",
        top: "40px",
        containLabel: true,
      },
      xAxis: {
        type: "time",
        axisLabel: {
          formatter: (value: number) => {
            if (aggregationType === "monthly") {
              return echarts.time.format(value, "{MMM} {yyyy}", false);
            } else if (aggregationType === "daily") {
              return echarts.time.format(value, "{MMM} {dd}", false);
            } else {
              return echarts.time.format(value, "{MMM} {dd}", false);
            }
          },
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: [
        {
          type: "value",
          name: "Energy (kWh)",
          position: "left",
          axisLabel: {
            formatter: "{value}",
          },
          splitLine: {
            show: true,
          },
        },
        {
          type: "value",
          name: "Battery Final Charge (kWh)",
          position: "right",
          axisLabel: {
            formatter: "{value}",
          },
          splitLine: {
            show: false,
          },
        },
      ],
      series: filteredChartData.map((series, index) => {
        const isRightAxis = series.axis === "right";
        const stackGroup = series.stacked
          ? isRightAxis
            ? `right-stack-${series.prefix}`
            : `left-stack-${series.prefix}`
          : undefined;

        const seriesConfig: any = {
          name: series.name,
          type: series.stacked ? "bar" : "line",
          yAxisIndex: isRightAxis ? 1 : 0,
          data: series.data.map((point) => [point.x.getTime(), point.y]),
          itemStyle: {
            color: series.color,
          },
          emphasis: {
            focus: "series",
          },
        };

        // Add stacking configuration only for stacked bar charts
        if (series.stacked) {
          seriesConfig.stack = stackGroup;
          // Force bars to use full available width with slight overlap
          seriesConfig.barCategoryGap = -1;
          seriesConfig.barGap = -1;
        } else {
          // Non-stacked series: render as line showing the top edge of bars
          seriesConfig.type = "line";
          seriesConfig.step = "end"; // Step up at the end, matching ChartProcessed
          seriesConfig.lineStyle = {
            color: series.color,
            width: 1,
            type: series.prefix === "compare" ? "dashed" : "solid",
          };
          seriesConfig.symbol = "none"; // Hide data point markers
          seriesConfig.sampling = "none"; // Ensure all data points are shown
        }

        // Add midnight vertical lines to the first series only
        if (index === 0) {
          seriesConfig.markLine = {
            silent: true,
            symbol: "none",
            label: {
              show: false, // Hide labels on vertical lines
            },
            lineStyle: {
              color: "#e0e0e0",
              width: 1,
              type: "solid",
              opacity: 0.5,
            },
            data: midnightLines,
          };
        }

        return seriesConfig;
      }),
    };

    return (
      <div
        style={{
          height,
          width: "100%",
        }}
      >
        <ReactECharts
          option={echartsOptions}
          style={{ height: "100%", width: "100%" }}
          opts={{ renderer: "canvas" }}
        />
      </div>
    );
  };

  // Legend component that can be reused
  const LegendComponent = () => (
    <>
      <div
        className="mt-3 mb-3 d-flex justify-left gap-2 flex-wrap d-flex-column"
        style={{ fontSize: "12px" }}
      >
        {[site.id, compareSite?.id]
          .filter((id) => !!id)
          .map((id) => id as string)
          .map((siteId) => {
            const siteName =
              siteId === site.id ? site.name : compareSite?.name || "";
            return (
              <React.Fragment key={siteId}>
                <div key={siteId} style={{ fontWeight: "bold", width: "100%" }}>
                  <span
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      const prefix = siteId === site.id ? "main" : "compare";
                      setHiddenPrefix((prev) => {
                        const newState = {
                          ...prev,
                          [prefix]: !prev[prefix],
                        };
                        return newState;
                      });
                    }}
                  >
                    {siteName}
                  </span>
                  {siteId !== site.id && (
                    <span>
                      &nbsp;- (<Link to={`/site/${siteId}/edit`}>Link</Link>)
                    </span>
                  )}
                </div>

                {chartData
                  .filter((series) => series.siteId === siteId)
                  .map((series) => (
                    <div
                      key={series.id}
                      className={`${!(hiddenSeries[series.id] ?? series.defaultHidden) && !(hiddenPrefix[series.prefix] ?? false) ? "" : "text-muted"}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <span
                        onClick={() => toggleSeries(series.id)}
                        style={{
                          cursor: "pointer",
                        }}
                      >
                        <div
                          className="me-2"
                          style={{
                            display: "inline-block",
                            width: "8px",
                            height: "8px",
                            backgroundColor:
                              !(
                                hiddenSeries[series.id] ?? series.defaultHidden
                              ) && !(hiddenPrefix[series.prefix] ?? false)
                                ? series.color
                                : "#ccc",
                          }}
                        />
                        {series.name}
                      </span>
                    </div>
                  ))}
              </React.Fragment>
            );
          })}
      </div>
      <div style={{ fontSize: "12px", marginBottom: "8px" }}>
        Groups:{" "}
        {["Generation", "Demand", "Battery Charge", "Export"].map((group) => (
          <div
            key={group}
            style={{ display: "inline-block", marginRight: "10px" }}
          >
            <span
              onClick={() => {
                const hiddenSeriesNotInGroup = chartData.reduce(
                  (acc, series) => {
                    acc[series.id] = !series.groups.includes(group);
                    return acc;
                  },
                  {}
                );
                setHiddenSeries(hiddenSeriesNotInGroup);
              }}
              style={{ cursor: "pointer", textDecoration: "underline" }}
            >
              {group}
            </span>
          </div>
        ))}
      </div>
    </>
  );

  // Time controls component that can be reused
  const TimeControls = ({ idPrefix = "" }: { idPrefix?: string }) => (
    <>
      <Col md={3} className="mb-3">
        <Form.Group controlId={`${idPrefix}timePeriod`}>
          <Form.Label>Time Period</Form.Label>
          <Form.Select
            value={timePeriod}
            onChange={(e) => handleTimePeriodChange(e.target.value)}
          >
            {timePresets.map((preset) => (
              <option
                key={preset.key}
                value={`${preset.key}|${preset.start}|${preset.end}`}
              >
                {preset.label}
              </option>
            ))}
            <option value="custom">Custom</option>
          </Form.Select>
        </Form.Group>
      </Col>
      <Col md={3} className="mb-3">
        <Form.Group controlId={`${idPrefix}startDate`}>
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => handleDateChange(e.target.value, endDate)}
            min="2024-01-01"
            max="2024-12-31"
          />
        </Form.Group>
      </Col>
      <Col md={3} className="mb-3">
        <Form.Group controlId={`${idPrefix}endDate`}>
          <Form.Label>End Date</Form.Label>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => handleDateChange(startDate, e.target.value)}
            min="2024-01-01"
            max="2024-12-31"
          />
        </Form.Group>
      </Col>
      <Col md={3} className="mb-3">
        <Form.Group controlId={`${idPrefix}aggregation`}>
          <Form.Label>Aggregation</Form.Label>
          <Form.Select
            value={aggregationType}
            onChange={(e) =>
              setAggregationType(
                e.target.value as "half-hourly" | "daily" | "monthly"
              )
            }
          >
            <option value="half-hourly">Half-Hourly</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
          </Form.Select>
        </Form.Group>
      </Col>
    </>
  );

  return (
    <CollapsibleCard
      title={`Graph forecast data`}
      className="mt-2"
      initialOpen={false}
      menu={
        <Dropdown>
          <Dropdown.Toggle
            variant="secondary"
            id="dropdown-basic"
            style={{ paddingTop: "2px", paddingBottom: "2px" }}
          >
            Downloads
          </Dropdown.Toggle>

          <Dropdown.Menu>
            <Dropdown.Item
              onClick={() =>
                fetchSiteReportPreview.mutate({
                  reportName: "energy_flows",
                  fileName: "energy-flows",
                  siteName: site.name,
                  options: {
                    groupByDay: false,
                    actuals: false,
                    year: 1,
                  },
                  payload: {
                    site: buildSiteCalculationPayload({
                      site: site as unknown as Partial<Site>,
                      clearNonCalcFields: false,
                    }),
                  },
                })
              }
            >
              Forecast site energy flow (HH)
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() =>
                fetchSiteReportPreview.mutate({
                  reportName: "energy_flows",
                  fileName: "energy-flows",
                  siteName: site.name,
                  options: {
                    groupByDay: true,
                    actuals: false,
                    year: 1,
                  },
                  payload: {
                    site: buildSiteCalculationPayload({
                      site: site as unknown as Partial<Site>,
                      clearNonCalcFields: false,
                    }),
                  },
                })
              }
            >
              Forecast site energy flow (Daily)
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      }
    >
      <Row className="d-flex flex-wrap align-items-end">
        <TimeControls />
        <ESysSelect
          site={site}
          selectedEsysIdx={selectedEsysIdx}
          setSelectedEsysIdx={setSelectedEsysIdx}
          showWholeSiteOption={true}
          wholeSiteLabel="Whole site"
        />
        <Col md={2} className="d-flex align-items-end gap-2 mb-3">
          <Button
            variant="primary"
            onClick={() => {
              if (!shouldFetch) {
                setShouldFetch(true);
              } else {
                setStartDate("2024-01-01");
                setEndDate("2024-12-31");
                setTimePeriod("full-year");
                setShouldFetch(false);
                setSelectedCompareSite([]);
                setSelectedCompareSiteEsysIdx("all");
                setChartWidth("100%");
                setHiddenSeries({});
                setHiddenPrefix({});
              }
            }}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : shouldFetch ? "Disable" : "Enable"}
          </Button>
          {shouldFetch && chartData.length > 0 && (
            <>
              <Button
                variant="outline-primary"
                onClick={() => setIsMaximized(true)}
                title="Maximize chart"
              >
                ⛶
              </Button>
            </>
          )}
        </Col>
      </Row>
      <Row className="mt-2">
        <Col>
          <Form.Group controlId="compareSite">
            <Form.Label>Compare with Site</Form.Label>
            <Typeahead
              id="site-selector"
              labelKey="name"
              options={siteOptions}
              placeholder="Select a site to compare..."
              selected={selectedCompareSite.slice(0, 1)}
              onChange={(selected) => {
                setSelectedCompareSite(selected);
                setSelectedCompareSiteEsysIdx("all");
              }}
              clearButton
              disabled={fetchSites.isLoading}
            />
          </Form.Group>
        </Col>
        {fetchCompareSite.isSuccess && fetchCompareSite.data.site && (
          <ESysSelect
            site={fetchCompareSite.data.site}
            selectedEsysIdx={selectedCompareSiteEsysIdx}
            setSelectedEsysIdx={setSelectedCompareSiteEsysIdx}
            showWholeSiteOption={true}
            wholeSiteLabel="Whole site"
          />
        )}
      </Row>

      {isLoading && (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2">Loading chart data...</div>
        </div>
      )}

      {!!error && (
        <div className="alert alert-danger" role="alert">
          Error loading half-hourly data:{" "}
          {String(error instanceof Error ? error.message : error)}
        </div>
      )}

      {shouldFetch && (chartDataQuery.data || compareChartDataQuery.data) && (
        <LegendComponent />
      )}

      {!isMaximized && shouldFetch && chartData.length > 0 && (
        <ChartComponent />
      )}

      {shouldFetch &&
        (chartDataQuery.data || compareChartDataQuery.data) &&
        chartData.length === 0 && (
          <div className="text-center py-4 text-muted">
            No data available for the selected date range.
          </div>
        )}

      <Modal
        show={isMaximized}
        onHide={() => setIsMaximized(false)}
        size="xl"
        fullscreen
      >
        <Modal.Header closeButton>
          <Modal.Title>Half-Hourly Data - Full Screen</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "calc(100vh - 120px)" }}>
          <div
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Time Period Controls */}
            <Row className="mb-3">
              <TimeControls idPrefix="modal-" />
              <Col md={2} className="d-flex align-items-end">
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    id="chart-width-dropdown"
                  >
                    Width: {chartWidth}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setChartWidth("100%")}>
                      100% (Normal)
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setChartWidth("600%")}>
                      600% (6x)
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>

            <LegendComponent />
            <div
              style={{
                overflowX: chartWidth !== "100%" ? "auto" : "visible",
                flex: "1 1 auto",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: chartWidth,
                  minWidth: chartWidth !== "100%" ? chartWidth : "auto",
                }}
              >
                <ChartComponent height="calc(100% - 100px)" />
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </CollapsibleCard>
  );
};

export default ChartForecast;
